import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { messaging } from "./firebase";
import { getToken } from "firebase/messaging";
import ViewTeams from './ViewTeams';

// Page components
import Home from "./Home";
import Login from "./Login";
import SignUp from "./SignUp";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import ChangePassword from "./ChangePassword";
import Profile from "./Profile";

// Game & Group components
import CreateGame from "./CreateGame";
import Games from "./Games";
import ViewGames from "./ViewGames";
import CreateGroup from "./CreateGroup";
import Groups from "./Groups";
import GroupDetail from "./GroupDetail";
import AddMember from "./AddMember";
import GameDetails from "./GameDetails";
import EditGame from "./EditGame";
import EditTeam from "./EditTeam";
import MakeTeams from './MakeTeams';

// Misc
import InvitePage from "./InvitePage";
import Notifications from "./Notifications";

// Chat components
import ChatLanding from "./chat/ChatLanding";
import GroupChat from "./chat/GroupChat";
import DirectMessageList from "./chat/DirectMessageList";
import GroupChatList from "./chat/GroupChatList";
import GroupChatWrapper from "./chat/GroupChatWrapper";
import ChatWindow from "./chat/ChatWindow";

// Styles
import "./style.css";

// ✅ Defined OUTSIDE the component — no scope issues
function requestNotificationPermission() {
  console.log("🔔 requestNotificationPermission called");

  if (!("Notification" in window)) {
    console.warn("Browser does not support notifications.");
    return;
  }

  if (Notification.permission === "denied") {
    console.warn("Notifications blocked by user.");
    return;
  }

  if (Notification.permission === "granted") {
    getToken(messaging, {
      vapidKey: "BEE86sjp3_dNwDVQQEsIhXgpr-DvCsbPz_xFty3sV4Gxbk--8M8iT9qgA30zEiK3Fu6GGN6rDc4b1CakjlceTis"
    })
      .then(token => console.log("🔥 Device Token:", token))
      .catch(err => console.error("Error getting token:", err));
    return;
  }

  // Permission is "default" — ask the user
  Notification.requestPermission().then(async (permission) => {
    if (permission === "granted") {
      try {
          const token = await getToken(messaging, {
  vapidKey: "BEE86sjp3_dNwDVQQEsIhXgpr-DvCsbPz_xFty3sV4Gxbk--8M8iT9qgA30zEiK3Fu6GGN6rDc4b1CakjlceTis"
});
console.log("🔥 Device Token:", token);

// Save token to backend
const authToken = localStorage.getItem("authToken");
if (authToken) {
  await fetch("/api/users/fcm-token", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`
    },
    body: JSON.stringify({ fcmToken: token })
  });
  console.log("✅ FCM token saved to backend");
}

        // TODO: Send token to your backend and save in Postgres
      } catch (err) {
        console.error("Error getting FCM token:", err);
      }
    }
  });
}

const AppContent = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const location = useLocation();

  const fetchCurrentUser = useCallback(async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setCurrentUser(null);
      setLoadingUser(false);
      return;
    }

    setLoadingUser(true);
    try {
      const res = await fetch("/api/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        setCurrentUser(null);
      } else {
        const data = await res.json();
        setCurrentUser(data);
      }
    } catch (err) {
      console.error("Error fetching current user:", err);
      setCurrentUser(null);
    } finally {
      setLoadingUser(false);
    }
  }, []);

  // ✅ Fetch user on route change
  useEffect(() => {
    if (!currentUser) {
      fetchCurrentUser();
    }
  }, [location.pathname, currentUser, fetchCurrentUser]);

  // ✅ Request notification permission once on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      requestNotificationPermission();
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Routes>
        {/* Auth & Profile */}
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/profile" element={<Profile />} />

        {/* Games & Groups */}
        <Route path="/create-game" element={<CreateGame />} />
        <Route path="/games" element={<Games />} />
        <Route path="/view-games" element={<ViewGames />} />
        <Route path="/games/:gameId" element={<GameDetails />} />
        <Route path="/edit-game/:gameId" element={<EditGame />} />
        <Route path="/edit-team/:gameId" element={<EditTeam />} />
        <Route path="/games/:gameId/make-teams" element={<MakeTeams />} />

        <Route path="/groups/create-group" element={<CreateGroup />} />
        <Route path="/groups" element={<Groups />} />
        <Route path="/group/:id" element={<GroupDetail />} />
        <Route path="/group/:id/add-member" element={<AddMember />} />

        {/* View Games */}
        <Route path="/games/:gameId/view-teams" element={<ViewTeams />} />

        {/* Misc */}
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/notifications" element={<Notifications />} />

        {/* Chat */}
        <Route path="/chat" element={<ChatLanding currentUser={currentUser} loadingUser={loadingUser} />} />
        <Route path="/chat/groups/:groupId" element={<GroupChat currentUser={currentUser} loadingUser={loadingUser} />} />
        <Route path="/chat/groups" element={<GroupChatList currentUser={currentUser} loadingUser={loadingUser} />} />
        <Route path="/chat/group/:groupId" element={<GroupChatWrapper currentUser={currentUser} loadingUser={loadingUser} />} />
        <Route path="/chat/direct/:userId" element={<ChatWindow chatType="direct" currentUser={currentUser} loadingUser={loadingUser} />} />
        <Route path="/chat/direct" element={<DirectMessageList currentUser={currentUser} loadingUser={loadingUser} />} />
        <Route path="/chat/:chatId" element={<ChatWindow chatType="group" currentUser={currentUser} loadingUser={loadingUser} />} />
      </Routes>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
    </>
  );
};

const App = () => (
  <Router>
    <AppContent />
  </Router>
);

export default App;
